import { useMemo, useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MapPin, MagnifyingGlass, ArrowLeft, ArrowRight, CaretRight, Buildings } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { useSeo } from "@/lib/seo";
import { useLanguage } from "@/context/LanguageContext";
import { INDIA_GEO, TOTAL_STATES, TOTAL_DISTRICTS } from "@/data/indiaGeo";

const toSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const HUES = [
  "from-orange-500 to-rose-600", "from-emerald-500 to-teal-700", "from-indigo-500 to-violet-700",
  "from-amber-500 to-orange-700", "from-sky-500 to-blue-700", "from-fuchsia-500 to-purple-700",
  "from-lime-500 to-green-700", "from-cyan-500 to-teal-700", "from-red-500 to-rose-700",
];
const hueFor = (i) => HUES[i % HUES.length];

export default function IndiaLocations() {
  useSeo("Browse alumni by location — India", "Find schools, colleges and alumni communities in every Indian state and district.");
  const { t } = useLanguage();
  const { stateSlug } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const dLabel = (n) => `${n} ${t(n === 1 ? "district" : "districts")}`;

  const selectedState = useMemo(
    () => (stateSlug ? INDIA_GEO.find((s) => toSlug(s.state) === stateSlug) : null),
    [stateSlug]
  );

  useEffect(() => { window.scrollTo(0, 0); }, [stateSlug]);

  // Global search across every district in the country.
  const districtMatches = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (term.length < 2) return [];
    const out = [];
    for (const s of INDIA_GEO) {
      for (const d of s.districts) {
        if (d.toLowerCase().includes(term) || s.state.toLowerCase().includes(term)) {
          out.push({ state: s.state, district: d });
        }
      }
    }
    return out.slice(0, 60);
  }, [q]);

  return (
    <div className="mx-auto max-w-7xl px-5 py-10" data-testid="india-locations-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{t("Browse by location")}</p>
          <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
            {selectedState ? selectedState.state : t("Every state & district in India")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {selectedState
              ? dLabel(selectedState.districts.length)
              : `${TOTAL_STATES} ${t("states & UTs")} · ${TOTAL_DISTRICTS}+ ${t("districts")}`}
          </p>
        </div>
        {selectedState && (
          <button onClick={() => navigate("/india/locations")} data-testid="locations-back"
            className="inline-flex items-center gap-1.5 rounded-full border border-border/70 px-4 py-2 text-sm font-medium transition-colors hover:border-primary/50 hover:text-primary">
            <ArrowLeft size={15} /> {t("All states")}
          </button>
        )}
      </div>

      {/* Global district search */}
      <div className="relative mt-6 max-w-xl">
        <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="location-search"
          placeholder={t("Search any district or state…")} className="rounded-full pl-11" />
      </div>

      {q.trim().length >= 2 ? (
        <div className="mt-6" data-testid="location-search-results">
          {districtMatches.length === 0 ? (
            <p className="text-muted-foreground">{t("No matching districts.")}</p>
          ) : (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
              {districtMatches.map((m) => (
                <Link key={m.state + m.district} to={`/india/place/${toSlug(m.state)}/${toSlug(m.district)}`}
                  data-testid={`district-result-${toSlug(m.district)}`}
                  className="flex items-center justify-between gap-2 rounded-xl border border-border/60 px-3.5 py-3 transition-colors hover:border-primary/50 hover:bg-accent/40">
                  <span className="min-w-0"><span className="block truncate text-sm font-medium">{m.district}</span><span className="text-xs text-muted-foreground">{m.state}</span></span>
                  <CaretRight size={15} className="shrink-0 text-muted-foreground" />
                </Link>
              ))}
            </div>
          )}
        </div>
      ) : selectedState ? (
        <>
          <Link to={`/explore/institutions?country=India&state=${encodeURIComponent(selectedState.state)}`}
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-secondary hover:underline" data-testid="view-state-institutions">
            <Buildings size={15} /> {t("View all institutions in")} {selectedState.state} <ArrowRight size={14} />
          </Link>
          <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5" data-testid="district-grid">
            {selectedState.districts.map((d) => (
              <Link key={d} to={`/india/place/${toSlug(selectedState.state)}/${toSlug(d)}`}
                data-testid={`district-tile-${toSlug(d)}`}
                className="flex items-center justify-between gap-2 rounded-xl border border-border/60 px-3.5 py-3 transition-colors hover:border-primary/50 hover:bg-accent/40">
                <span className="truncate text-sm font-medium">{d}</span>
                <CaretRight size={15} className="shrink-0 text-muted-foreground" />
              </Link>
            ))}
          </div>
        </>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" data-testid="state-grid-full">
          {INDIA_GEO.map((s, i) => (
            <Link key={s.state} to={`/india/locations/${toSlug(s.state)}`}
              data-testid={`state-card-${toSlug(s.state)}`}
              className="group relative overflow-hidden rounded-2xl border border-border/60 p-3.5 transition-transform duration-200 hover:-translate-y-0.5 hover:border-primary/40">
              <div className={`absolute inset-0 bg-gradient-to-br ${hueFor(i)} opacity-[0.92]`} />
              <div className="relative flex h-full flex-col justify-between text-white">
                <MapPin size={18} weight="duotone" className="opacity-90" />
                <div className="mt-6">
                  <p className="font-heading text-sm font-semibold leading-tight">{s.state}</p>
                  <p className="mt-0.5 text-[11px] font-medium text-white/90">{dLabel(s.districts.length)}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-dashed border-secondary/40 bg-secondary/5 p-5 text-center" data-testid="not-found-cta">
        <p className="text-sm font-medium text-foreground">{t("Not found your school / college?")}</p>
        <Link to="/institutions/suggest" className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:underline" data-testid="suggest-from-locations">
          {t("Give us the information and we'll build one")} <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
